import { requireKnownEnumValue } from "@/lib/enum";
import { FailurePosture as KnownFailurePosture } from "@gram/client/models/components/createinstancerequestbody.js";
import type { LiteLLMInstanceFailurePosture } from "@gram/client/models/components/litellminstance.js";

function apiBase(serverURL: string): string {
  const url = new URL(serverURL);
  const ipv4Parts = url.hostname.split(".");
  const ipv4Loopback =
    ipv4Parts.length === 4 &&
    ipv4Parts[0] === "127" &&
    ipv4Parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
  const loopback =
    url.hostname === "localhost" || url.hostname === "[::1]" || ipv4Loopback;
  if (url.protocol !== "https:" && !(url.protocol === "http:" && loopback)) {
    throw new Error("LiteLLM integration endpoints require HTTPS");
  }
  return url.toString().replace(/\/$/, "");
}

export function buildLiteLLMGuardrailConfig(
  serverURL: string,
  failurePosture: LiteLLMInstanceFailurePosture,
): string {
  const supportedPosture = requireKnownEnumValue(
    KnownFailurePosture,
    failurePosture,
  );
  return `guardrails:
  - guardrail_name: gram-risk
    litellm_params:
      guardrail: generic_guardrail_api
      mode: [pre_call, post_call]
      api_base: ${apiBase(serverURL)}/rpc/litellm.ingest
      headers:
        Speakeasy-AI-Key: os.environ/SPEAKEASY_AI_LITELLM_INGEST_KEY
        Speakeasy-AI-Project: os.environ/SPEAKEASY_AI_PROJECT
      extra_headers:
        - speakeasy-ai-session-id
        - x-claude-code-session-id
        - session-id
        - thread-id
        - x-session-id
        - speakeasy-ai-agent-provider
        - speakeasy-ai-agent-session-id
        - speakeasy-ai-agent-turn-id
        - x-codex-turn-metadata
        - x-opencode-session
        - x-opencode-request
        # Deprecated names that clients set up before the rename still send.
        - x-gram-session-id
        - x-gram-agent-provider
        - x-gram-agent-session-id
        - x-gram-agent-turn-id
      default_on: true
      streaming_end_of_stream_only: true
      fail_on_error: true
      unreachable_fallback: ${supportedPosture}`;
}

export function buildLiteLLMEnvironment(
  serverURL: string,
  projectSlug: string,
): string {
  return `export SPEAKEASY_AI_LITELLM_INGEST_KEY="<PASTE_KEY_SHOWN_ABOVE>"
export SPEAKEASY_AI_PROJECT="${projectSlug}"
export LITELLM_OTEL_V2=true
export OTEL_EXPORTER=otlp_http
export OTEL_ENDPOINT="${apiBase(serverURL)}/rpc/hooks.otel"
export OTEL_HEADERS="Speakeasy-AI-Key=\${SPEAKEASY_AI_LITELLM_INGEST_KEY},Speakeasy-AI-Project=\${SPEAKEASY_AI_PROJECT}"
export OTEL_SERVICE_NAME=litellm
export OTEL_INSTRUMENTATION_GENAI_CAPTURE_MESSAGE_CONTENT=no_content
export LITELLM_OTEL_INTEGRATION_ENABLE_METRICS=true
export LITELLM_OTEL_LEGACY_COMPAT=false`;
}

export const liteLLMVerificationCommands = {
  safe: `curl "\${LITELLM_PROXY_URL:-http://localhost:4000}/v1/chat/completions" \\
  --header "Authorization: Bearer $LITELLM_VIRTUAL_KEY" \\
  --header "Content-Type: application/json" \\
  --data '{"model":"'"$LITELLM_MODEL"'","messages":[{"role":"user","content":"Reply with OK."}]}'`,
  blocked: `curl "\${LITELLM_PROXY_URL:-http://localhost:4000}/v1/chat/completions" \\
  --header "Authorization: Bearer $LITELLM_VIRTUAL_KEY" \\
  --header "Content-Type: application/json" \\
  --data '{"model":"'"$LITELLM_MODEL"'","messages":[{"role":"user","content":"token=ghp_R2D2C3POLuk3Skywalker1234567890ab"}]}'`,
} as const;
